import { useEffect, useMemo, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { api } from '../api/client'
import { LoadingCards, EmptyBlock, ErrorBlock, Pill, useLoadable } from '../components/ui'
import { FolderDetail, FolderTreePane } from '../components/FolderTree'
import { buildExplorer, driveCodes, indexTree, withLive } from './myAccessTree'
import type { AccessDrive, MyAccess } from '../types'

function hhmm(iso: string) {
  return new Date(iso).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
}

const codeLabel = (c: string) => (c === 'HUB' ? 'Hub' : c === 'GROUP' ? 'Group' : c)

export default function MyAccessRoute({ moveEnabled = false, filesEnabled = false }: { moveEnabled?: boolean; filesEnabled?: boolean }) {
  /* A1: Refresh re-asks Google (POST /refresh), bypassing the 45–90 min cache,
     so access granted a minute ago shows up. First load and error retry stay
     cache-aware. */
  const forceLive = useRef(false)
  const [access, retry] = useLoadable<MyAccess>(
    () => {
      const live = forceLive.current
      forceLive.current = false
      return live ? api.refreshAccess() : api.getAccess()
    },
    (d) => d.drives.every((dr) => dr.folders.length === 0),
    [],
  )
  const refresh = () => {
    forceLive.current = true
    retry()
  }
  /* Explorer (Tony, 21 Oct 2026): the selected folder (?f=) and country (?c=) live in the URL,
     so Back steps out of a folder and a link opens the same place. */
  const [params, setParams] = useSearchParams()
  const data = access.status === 'ready' ? access.data : null
  /* Epic v4 A2 (Tony, 3 Nov 2026): the page shows at once; folders made or shared in the last day
     that the portal has not registered yet are asked of Drive afterwards and slot in, marked New. */
  const [live, setLive] = useState<{ for: MyAccess; drives: AccessDrive[] } | null>(null)
  useEffect(() => {
    if (!data) return
    let gone = false
    api.getAccessLive().then((r) => { if (!gone && r?.drives.length) setLive({ for: data, drives: r.drives }) })
    return () => { gone = true }
  }, [data])
  const roots = useMemo(
    () => (data ? buildExplorer(withLive(data.drives, live?.for === data ? live.drives : null)) : []),
    [data, live],
  )
  const index = useMemo(() => indexTree(roots), [roots])

  if (access.status === 'loading') {
    return (
      <>
        <p className="eyebrow">My access</p>
        <h1>Resolving your access…</h1>
        <div className="asof"><span>Asking Google what you can reach</span></div>
        <LoadingCards count={6} label="Resolving your folder access" />
      </>
    )
  }

  if (access.status === 'error') {
    return <ErrorBlock message={access.message} correlationId={access.correlationId} onRetry={retry} />
  }

  /* S4-1: a brand new hire with zero entitled folders gets a designed first-run
     state naming their department and its owner — not an empty list. */
  if (access.status === 'empty') {
    return (
      <>
        <p className="eyebrow">My access</p>
        <h1>You are not in any folder groups yet</h1>
        <EmptyBlock
          title="This is normal on your first day"
          action={<button className="btn btn--primary">Ask my department lead</button>}
        >
          Your department lead adds you to the folders your role needs. Until then you can
          still browse the full folder map under Owners and request anything you need.
        </EmptyBlock>
      </>
    )
  }

  const d = access.data
  const codes = driveCodes(roots)
  const country = codes.includes(params.get('c') ?? '') ? params.get('c')! : 'ALL'
  const shown = country === 'ALL' ? roots : roots.filter((r) => r.drive.code === country)
  const requested = index.get(params.get('f') ?? '')
  const selected = requested && shown.some((r) => r.drive.code === requested.drive.code) ? requested : shown[0]
  const select = (id: string) => {
    const next = new URLSearchParams(params)
    next.set('f', id)
    setParams(next)
  }
  const pickCountry = (c: string) => {
    const next = new URLSearchParams(params)
    if (c === 'ALL') next.delete('c')
    else next.set('c', c)
    next.delete('f')
    setParams(next, { replace: true })
  }

  return (
    <>
      <p className="eyebrow">My access</p>
      <h1 style={{ marginBottom: 'var(--s-sm)' }}>What you can reach</h1>

      {/* A1: staleness is visible and self-serviceable rather than hidden. */}
      <div className="asof">
        <span>Access as of {hhmm(d.resolvedAt)}</span>
        <button onClick={refresh}>Refresh</button>
        <span>·</span>
        <span>Showing only the folders you can open. Need another one? Find it under <Link to="/owners">Owners</Link> and request access.</span>
      </div>

      {/* C3: unknown is reported as unknown, never as "no access". */}
      {d.unknownCount > 0 && (
        <div className="block block--narrow" style={{ marginBottom: 'var(--s-md)' }}>
          <Pill tone="amber">Could not check {d.unknownCount} folders</Pill>
          <p style={{ marginTop: 'var(--s-sm)', marginBottom: 0 }}>
            Google did not answer for some folders just now. They are marked as unchecked.
            This does not mean you have lost access to them.
          </p>
        </div>
      )}

      {codes.length > 1 && (
        <div className="ma-where" role="group" aria-label="Show folders in">
          <span className="ma-where__lbl">Where</span>
          {['ALL', ...codes].map((c) => (
            <button key={c} className="ma-chip" aria-pressed={country === c} onClick={() => pickCountry(c)}
              title={c === 'ALL' ? 'Every drive you can reach' : roots.find((r) => r.drive.code === c)?.name}>
              {c === 'ALL' ? 'All' : codeLabel(c)}
            </button>
          ))}
        </div>
      )}

      {selected && (
        <div className="ma-explorer">
          <FolderTreePane roots={shown} selectedId={selected.id} onSelect={select} />
          <FolderDetail node={selected} onSelect={select} getReverse={api.getReverse} moveEnabled={moveEnabled} filesEnabled={filesEnabled} />
        </div>
      )}
    </>
  )
}
